import { useEffect, useState, useCallback } from "react";
import { api, resolveAssetUrl } from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Save, Loader2 } from "lucide-react";
import { toast } from "sonner";
import ImageUploader from "@/components/admin/ImageUploader";
import RepeaterList from "@/components/admin/RepeaterList";
import RichTextEditor from "@/components/admin/RichTextEditor";
import { ICON_NAMES, Icon, SOCIAL_ICONS } from "@/components/Icon";

const FIELD_CLS = "space-y-1.5";

function Field({ label, children, hint }) {
  return (
    <div className={FIELD_CLS}>
      <Label className="text-xs uppercase tracking-wider text-ink-muted">{label}</Label>
      {children}
      {hint && <div className="text-[11px] text-ink-muted">{hint}</div>}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="card-flat p-6 sm:p-7 space-y-5">
      <h3 className="font-display text-xl font-bold">{title}</h3>
      {children}
    </div>
  );
}

function IconSelect({ value, onChange, testid }) {
  return (
    <Select value={value || ""} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className="h-9"><SelectValue placeholder="Icon"/></SelectTrigger>
      <SelectContent className="max-h-72">
        {ICON_NAMES.map(n => <SelectItem key={n} value={n}>{n}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

export default function AdminContent() {
  const [c, setC] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/content");
    setC(data || {});
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!c) return <div className="text-ink-muted">Loading content…</div>;

  const get = (path, def = "") => path.split(".").reduce((a, k) => (a && a[k] != null ? a[k] : undefined), c) ?? def;
  const set = (path, value) => {
    setC(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      const keys = path.split(".");
      let n = next;
      for (let i = 0; i < keys.length - 1; i++) {
        if (n[keys[i]] == null || typeof n[keys[i]] !== "object") n[keys[i]] = {};
        n = n[keys[i]];
      }
      n[keys[keys.length - 1]] = value;
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/content", c);
      setC(data);
      toast.success("Content saved");
    } catch (e) {
      toast.error("Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="overline">CMS</div>
          <h1 className="font-display text-3xl font-extrabold mt-1">Site content</h1>
          <p className="text-sm text-ink-muted mt-1">Edit every section of the website without touching code.</p>
        </div>
        <Button data-testid="content-save" className="btn-saffron rounded-full" disabled={saving} onClick={save}>
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin"/> : <Save className="h-4 w-4 mr-2"/>}
          Save changes
        </Button>
      </div>

      <Tabs defaultValue="branding" className="space-y-5">
        <div className="sticky top-0 z-20 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-3 bg-slate-50/95 backdrop-blur border-b border-slate-200">
          <TabsList className="flex flex-wrap h-auto p-1.5 bg-white border border-slate-200 rounded-full shadow-sm gap-1">
            {[
              ["branding","Brand"],["header","Header"],["hero","Hero"],["stats","Stats"],["marquee","Marquee"],
              ["why","Why TARA"],["cta","CTA"],["products","Products"],["services","Services"],["about","About"],
              ["partners","Partners"],["contact","Contact"],["emi","EMI"],["footer","Footer"],["whatsapp","WhatsApp"],
            ].map(([k,label]) =>
              <TabsTrigger key={k} value={k} data-testid={`tab-${k}`}
                className="rounded-full px-3.5 text-xs font-semibold data-[state=active]:bg-navy data-[state=active]:text-white">{label}</TabsTrigger>
            )}
          </TabsList>
        </div>

        <TabsContent value="branding" className="space-y-5">
          <Section title="Logo, Site name & Tagline">
            <div className="grid sm:grid-cols-2 gap-5">
              <ImageUploader testid="logo" label="Logo image" value={get("branding.logo_url")} onChange={v => set("branding.logo_url", v)}/>
              <Field label="Logo height (px)"><Input type="number" data-testid="branding-logo-h" value={get("branding.logo_height", 40)} onChange={e => set("branding.logo_height", Number(e.target.value) || 40)}/></Field>
              <Field label="Site name"><Input data-testid="branding-site-name" value={get("branding.site_name")} onChange={e => set("branding.site_name", e.target.value)}/></Field>
              <Field label="Tagline"><Input data-testid="branding-tagline" value={get("branding.tagline")} onChange={e => set("branding.tagline", e.target.value)}/></Field>
            </div>
          </Section>
        </TabsContent>

        <TabsContent value="header" className="space-y-5">
          <Section title="Header phone & Call-Support button">
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Phone number"><Input data-testid="header-phone" value={get("header.phone")} onChange={e => set("header.phone", e.target.value)}/></Field>
              <Field label="Button label (line 1)"><Input data-testid="header-label" value={get("header.call_label")} onChange={e => set("header.call_label", e.target.value)}/></Field>
              <div className="flex items-center justify-between rounded-xl bg-slate-50 p-3 sm:col-span-2">
                <div><div className="text-sm font-medium">Show Call Support button</div><div className="text-xs text-ink-muted">Toggle the dark phone button on the right of header.</div></div>
                <Switch data-testid="header-show" checked={!!get("header.show_call_button", true)} onCheckedChange={v => set("header.show_call_button", v)}/>
              </div>
            </div>
          </Section>
        </TabsContent>

        <TabsContent value="hero" className="space-y-5">
          <Section title="Hero text & CTAs">
            <Field label="Title"><Textarea rows={2} data-testid="hero-title" value={get("hero.title")} onChange={e => set("hero.title", e.target.value)}/></Field>
            <Field label="Subtitle" hint="Rich text supported — use the toolbar for bold / italic / lists / links etc.">
              <RichTextEditor testid="hero-subtitle" value={get("hero.subtitle")} onChange={v => set("hero.subtitle", v)}/>
            </Field>
            <Field label="Eyebrow chips (DSA, Loans, Advisory, India…)">
              <RepeaterList testid="chips" items={(get("hero.eyebrow_chips", [])).map((t, i) => ({ id: `c${i}`, text: t }))}
                onChange={(arr) => set("hero.eyebrow_chips", arr.map(x => x.text || ""))}
                makeNew={() => ({ text: "" })}
                render={(it, idx, setI) => (
                  <Input data-testid={`chip-${idx}`} value={it.text || ""} onChange={e => setI({ text: e.target.value })} placeholder="Label"/>
                )}
              />
            </Field>
            <div className="grid sm:grid-cols-2 gap-5">
              <div className="space-y-3">
                <div className="text-xs uppercase tracking-wider text-ink-muted">Primary CTA</div>
                <Input data-testid="hero-primary-label" value={get("hero.primary_cta.label")} onChange={e => set("hero.primary_cta.label", e.target.value)} placeholder="Label"/>
                <Select value={get("hero.primary_cta.action", "wizard")} onValueChange={v => set("hero.primary_cta.action", v)}>
                  <SelectTrigger><SelectValue/></SelectTrigger>
                  <SelectContent><SelectItem value="wizard">Open Loan Wizard</SelectItem><SelectItem value="link">Navigate to URL</SelectItem></SelectContent>
                </Select>
                {get("hero.primary_cta.action") === "link" &&
                  <Input value={get("hero.primary_cta.target")} onChange={e => set("hero.primary_cta.target", e.target.value)} placeholder="/some-page"/>}
              </div>
              <div className="space-y-3">
                <div className="text-xs uppercase tracking-wider text-ink-muted">Secondary CTA</div>
                <Input data-testid="hero-secondary-label" value={get("hero.secondary_cta.label")} onChange={e => set("hero.secondary_cta.label", e.target.value)} placeholder="Label"/>
                <Select value={get("hero.secondary_cta.action", "link")} onValueChange={v => set("hero.secondary_cta.action", v)}>
                  <SelectTrigger><SelectValue/></SelectTrigger>
                  <SelectContent><SelectItem value="wizard">Open Loan Wizard</SelectItem><SelectItem value="link">Navigate to URL</SelectItem></SelectContent>
                </Select>
                {get("hero.secondary_cta.action") === "link" &&
                  <Input value={get("hero.secondary_cta.target")} onChange={e => set("hero.secondary_cta.target", e.target.value)} placeholder="/emi-calculator"/>}
              </div>
            </div>
          </Section>

          <Section title="Hero photos (multiple)">
            <RepeaterList testid="hero-photos" addLabel="Add photo"
              items={(get("hero.photos", [])).map((u, i) => ({ id: `p${i}`, url: u }))}
              onChange={(arr) => set("hero.photos", arr.map(x => x.url || ""))}
              makeNew={() => ({ url: "" })}
              render={(it, idx, setI) => (
                <ImageUploader testid={`hero-photo-${idx}`} label={`Photo #${idx+1}`} value={it.url} onChange={v => setI({ url: v })}/>
              )}
            />
          </Section>

          <Section title="Floating top card (above photo)">
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Eyebrow"><Input data-testid="hero-topcard-eyebrow" value={get("hero.top_card.eyebrow")} onChange={e => set("hero.top_card.eyebrow", e.target.value)}/></Field>
              <Field label="Title"><Input data-testid="hero-topcard-title" value={get("hero.top_card.title")} onChange={e => set("hero.top_card.title", e.target.value)}/></Field>
            </div>
          </Section>

          <Section title="Floating bottom card (over photo)">
            <div className="grid sm:grid-cols-3 gap-5">
              <Field label="Eyebrow"><Input data-testid="hero-botcard-eyebrow" value={get("hero.bottom_card.eyebrow")} onChange={e => set("hero.bottom_card.eyebrow", e.target.value)}/></Field>
              <Field label="Title"><Input data-testid="hero-botcard-title" value={get("hero.bottom_card.title")} onChange={e => set("hero.bottom_card.title", e.target.value)}/></Field>
              <Field label="Sub-text"><Input data-testid="hero-botcard-sub" value={get("hero.bottom_card.sub")} onChange={e => set("hero.bottom_card.sub", e.target.value)}/></Field>
            </div>
          </Section>
        </TabsContent>

        <TabsContent value="stats" className="space-y-5">
          <Section title="Stats counters">
            <RepeaterList testid="stats" addLabel="Add stat"
              items={get("stats", [])} onChange={(arr) => set("stats", arr)}
              makeNew={() => ({ id: `s${Date.now()}`, label: "", value: 0, suffix: "+" })}
              render={(it, idx, setI) => (
                <div className="grid grid-cols-3 gap-3">
                  <Input data-testid={`stat-label-${idx}`} value={it.label || ""} onChange={e => setI({ label: e.target.value })} placeholder="Label"/>
                  <Input data-testid={`stat-value-${idx}`} type="number" value={it.value ?? ""} onChange={e => setI({ value: Number(e.target.value) || 0 })} placeholder="Value"/>
                  <Input data-testid={`stat-suffix-${idx}`} value={it.suffix || ""} onChange={e => setI({ suffix: e.target.value })} placeholder="Suffix (+, %, Cr+)"/>
                </div>
              )}
            />
          </Section>
        </TabsContent>

        <TabsContent value="marquee" className="space-y-5">
          <Section title="Scroller (below hero)">
            <Field label="Heading"><Input data-testid="marquee-heading" value={get("marquee.heading")} onChange={e => set("marquee.heading", e.target.value)}/></Field>
            <RepeaterList testid="marquee-items" addLabel="Add item"
              items={get("marquee.items", [])} onChange={(arr) => set("marquee.items", arr)}
              makeNew={() => ({ name: "", logo_url: "" })}
              render={(it, idx, setI) => (
                <div className="grid sm:grid-cols-2 gap-3">
                  <Input data-testid={`marq-name-${idx}`} value={it.name || ""} onChange={e => setI({ name: e.target.value })} placeholder="Brand name"/>
                  <ImageUploader testid={`marq-logo-${idx}`} label="Logo (optional)" value={it.logo_url} onChange={v => setI({ logo_url: v })}/>
                </div>
              )}
            />
          </Section>
        </TabsContent>

        <TabsContent value="why" className="space-y-5">
          <Section title="Why TARA section">
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Eyebrow"><Input data-testid="why-eyebrow" value={get("why_section.eyebrow")} onChange={e => set("why_section.eyebrow", e.target.value)}/></Field>
              <Field label="CTA label"><Input data-testid="why-cta-label" value={get("why_section.cta_label")} onChange={e => set("why_section.cta_label", e.target.value)}/></Field>
              <Field label="Heading"><Textarea rows={2} data-testid="why-heading" value={get("why_section.heading")} onChange={e => set("why_section.heading", e.target.value)}/></Field>
              <Field label="Blurb"><Textarea rows={3} data-testid="why-blurb" value={get("why_section.blurb")} onChange={e => set("why_section.blurb", e.target.value)}/></Field>
            </div>
            <RepeaterList testid="why-items" addLabel="Add benefit"
              items={get("why_section.items", [])} onChange={(arr) => set("why_section.items", arr)}
              makeNew={() => ({ icon: "Zap", title: "", desc: "" })}
              render={(it, idx, setI) => (
                <div className="grid sm:grid-cols-12 gap-3">
                  <div className="sm:col-span-3"><IconSelect testid={`why-icon-${idx}`} value={it.icon} onChange={v => setI({ icon: v })}/></div>
                  <div className="sm:col-span-3"><Input data-testid={`why-title-${idx}`} value={it.title || ""} onChange={e => setI({ title: e.target.value })} placeholder="Title"/></div>
                  <div className="sm:col-span-6"><Input data-testid={`why-desc-${idx}`} value={it.desc || ""} onChange={e => setI({ desc: e.target.value })} placeholder="Description"/></div>
                </div>
              )}
            />
          </Section>
        </TabsContent>

        <TabsContent value="cta" className="space-y-5">
          <Section title="'Ready to start?' band">
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Eyebrow"><Input data-testid="cta-eyebrow" value={get("cta_section.eyebrow")} onChange={e => set("cta_section.eyebrow", e.target.value)}/></Field>
              <Field label="Primary button"><Input data-testid="cta-primary" value={get("cta_section.primary_label")} onChange={e => set("cta_section.primary_label", e.target.value)}/></Field>
              <Field label="Heading"><Textarea rows={2} data-testid="cta-heading" value={get("cta_section.heading")} onChange={e => set("cta_section.heading", e.target.value)}/></Field>
              <Field label="Subtitle">
                <RichTextEditor testid="cta-subtitle" value={get("cta_section.subtitle")} onChange={v => set("cta_section.subtitle", v)}/>
              </Field>
              <Field label="Secondary button label (opens phone dialer)"><Input data-testid="cta-secondary-label" value={get("cta_section.secondary_label")} onChange={e => set("cta_section.secondary_label", e.target.value)}/></Field>
              <Field label="Secondary phone"><Input data-testid="cta-secondary-phone" value={get("cta_section.secondary_phone")} onChange={e => set("cta_section.secondary_phone", e.target.value)}/></Field>
            </div>
          </Section>
        </TabsContent>

        <TabsContent value="products" className="space-y-5">
          <Section title="Products section heading">
            <div className="grid sm:grid-cols-3 gap-5">
              <Field label="Eyebrow"><Input data-testid="prodsec-eyebrow" value={get("products_section.eyebrow")} onChange={e => set("products_section.eyebrow", e.target.value)}/></Field>
              <Field label="Heading"><Input data-testid="prodsec-heading" value={get("products_section.heading")} onChange={e => set("products_section.heading", e.target.value)}/></Field>
              <Field label="See-all label"><Input data-testid="prodsec-link" value={get("products_section.see_all_label")} onChange={e => set("products_section.see_all_label", e.target.value)}/></Field>
            </div>
          </Section>
          <Section title="Home page product cards">
            <RepeaterList testid="home-products" addLabel="Add product card"
              items={get("products", [])} onChange={(arr) => set("products", arr)}
              makeNew={() => ({ slug: "", title: "", icon: "Wallet", blurb: "" })}
              render={(it, idx, setI) => (
                <div className="grid sm:grid-cols-12 gap-3">
                  <div className="sm:col-span-2"><IconSelect testid={`hp-icon-${idx}`} value={it.icon} onChange={v => setI({ icon: v })}/></div>
                  <div className="sm:col-span-3"><Input data-testid={`hp-title-${idx}`} value={it.title || ""} onChange={e => setI({ title: e.target.value })} placeholder="Title"/></div>
                  <div className="sm:col-span-3"><Input data-testid={`hp-slug-${idx}`} value={it.slug || ""} onChange={e => setI({ slug: e.target.value })} placeholder="page slug"/></div>
                  <div className="sm:col-span-4"><Input data-testid={`hp-blurb-${idx}`} value={it.blurb || ""} onChange={e => setI({ blurb: e.target.value })} placeholder="Short blurb"/></div>
                </div>
              )}
            />
          </Section>
          <Section title="Products HUB page">
            <div className="grid sm:grid-cols-3 gap-5">
              <Field label="Eyebrow"><Input data-testid="ph-eyebrow" value={get("products_page.hero.eyebrow")} onChange={e => set("products_page.hero.eyebrow", e.target.value)}/></Field>
              <Field label="Title"><Input data-testid="ph-title" value={get("products_page.hero.title")} onChange={e => set("products_page.hero.title", e.target.value)}/></Field>
              <Field label="Subtitle"><Input data-testid="ph-subtitle" value={get("products_page.hero.subtitle")} onChange={e => set("products_page.hero.subtitle", e.target.value)}/></Field>
              <div className="sm:col-span-3"><ImageUploader testid="ph-image" label="Hero image" value={get("products_page.hero.image_url")} onChange={v => set("products_page.hero.image_url", v)}/></div>
            </div>
            <Field label="Description (rich text)" hint="Optional longer description shown under the hero on Products page.">
              <RichTextEditor testid="ph-description" value={get("products_page.hero.description")} onChange={v => set("products_page.hero.description", v)}/>
            </Field>
            <RepeaterList testid="ph-items" addLabel="Add card"
              items={get("products_page.items", [])} onChange={(arr) => set("products_page.items", arr)}
              makeNew={() => ({ slug: "", title: "", icon: "Wallet", blurb: "", rate: "" })}
              render={(it, idx, setI) => (
                <div className="grid sm:grid-cols-12 gap-3">
                  <div className="sm:col-span-2"><IconSelect testid={`ph-icon-${idx}`} value={it.icon} onChange={v => setI({ icon: v })}/></div>
                  <div className="sm:col-span-3"><Input data-testid={`ph-card-title-${idx}`} value={it.title || ""} onChange={e => setI({ title: e.target.value })} placeholder="Title"/></div>
                  <div className="sm:col-span-2"><Input data-testid={`ph-card-slug-${idx}`} value={it.slug || ""} onChange={e => setI({ slug: e.target.value })} placeholder="slug"/></div>
                  <div className="sm:col-span-3"><Input data-testid={`ph-card-blurb-${idx}`} value={it.blurb || ""} onChange={e => setI({ blurb: e.target.value })} placeholder="Blurb"/></div>
                  <div className="sm:col-span-2"><Input data-testid={`ph-card-rate-${idx}`} value={it.rate || ""} onChange={e => setI({ rate: e.target.value })} placeholder="Rate"/></div>
                </div>
              )}
            />
          </Section>
        </TabsContent>

        <TabsContent value="services" className="space-y-5">
          <Section title="Services hub page">
            <div className="grid sm:grid-cols-3 gap-5">
              <Field label="Eyebrow"><Input data-testid="sv-eyebrow" value={get("services_page.hero.eyebrow")} onChange={e => set("services_page.hero.eyebrow", e.target.value)}/></Field>
              <Field label="Title"><Input data-testid="sv-title" value={get("services_page.hero.title")} onChange={e => set("services_page.hero.title", e.target.value)}/></Field>
              <Field label="Subtitle"><Input data-testid="sv-subtitle" value={get("services_page.hero.subtitle")} onChange={e => set("services_page.hero.subtitle", e.target.value)}/></Field>
              <div className="sm:col-span-3"><ImageUploader testid="sv-image" label="Hero image" value={get("services_page.hero.image_url")} onChange={v => set("services_page.hero.image_url", v)}/></div>
            </div>
            <Field label="Description (rich text)" hint="Optional longer description shown under the hero on Services page.">
              <RichTextEditor testid="sv-description" value={get("services_page.hero.description")} onChange={v => set("services_page.hero.description", v)}/>
            </Field>
            <RepeaterList testid="sv-items" addLabel="Add service"
              items={get("services_page.items", [])} onChange={(arr) => set("services_page.items", arr)}
              makeNew={() => ({ slug: "", title: "", icon: "BadgeCheck", blurb: "" })}
              render={(it, idx, setI) => (
                <div className="grid sm:grid-cols-12 gap-3">
                  <div className="sm:col-span-2"><IconSelect testid={`sv-icon-${idx}`} value={it.icon} onChange={v => setI({ icon: v })}/></div>
                  <div className="sm:col-span-3"><Input data-testid={`sv-card-title-${idx}`} value={it.title || ""} onChange={e => setI({ title: e.target.value })} placeholder="Title"/></div>
                  <div className="sm:col-span-2"><Input data-testid={`sv-card-slug-${idx}`} value={it.slug || ""} onChange={e => setI({ slug: e.target.value })} placeholder="slug"/></div>
                  <div className="sm:col-span-5"><Input data-testid={`sv-card-blurb-${idx}`} value={it.blurb || ""} onChange={e => setI({ blurb: e.target.value })} placeholder="Blurb"/></div>
                </div>
              )}
            />
          </Section>
        </TabsContent>

        <TabsContent value="about" className="space-y-5">
          <Section title="About hero">
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Eyebrow"><Input data-testid="ab-eyebrow" value={get("about_page.hero.eyebrow")} onChange={e => set("about_page.hero.eyebrow", e.target.value)}/></Field>
              <Field label="Title"><Textarea rows={2} data-testid="ab-title" value={get("about_page.hero.title")} onChange={e => set("about_page.hero.title", e.target.value)}/></Field>
              <ImageUploader testid="ab-image" label="Hero / core image" value={get("about_page.hero.image_url")} onChange={v => set("about_page.hero.image_url", v)}/>
            </div>
            <Field label="Subtitle (rich text)" hint="Use the toolbar for bold, lists, headings, links etc.">
              <RichTextEditor testid="ab-subtitle" value={get("about_page.hero.subtitle")} onChange={v => set("about_page.hero.subtitle", v)} minHeight="min-h-[160px]"/>
            </Field>
          </Section>
          <Section title="Vision & Mission">
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Vision title"><Input data-testid="ab-vt" value={get("about_page.vision_title")} onChange={e => set("about_page.vision_title", e.target.value)}/></Field>
              <Field label="Mission title"><Input data-testid="ab-mt" value={get("about_page.mission_title")} onChange={e => set("about_page.mission_title", e.target.value)}/></Field>
            </div>
            <Field label="Vision text (rich text)">
              <RichTextEditor testid="ab-vtext" value={get("about_page.vision_text")} onChange={v => set("about_page.vision_text", v)}/>
            </Field>
            <Field label="Mission text (rich text)">
              <RichTextEditor testid="ab-mtext" value={get("about_page.mission_text")} onChange={v => set("about_page.mission_text", v)}/>
            </Field>
          </Section>
          <Section title="Home page — About band">
            <div className="text-xs text-ink-muted -mt-2 mb-2">Controls the About-section that appears on the <strong>Home page</strong>. Leave any field blank to fall back to the matching About page field above.</div>
            <ImageUploader testid="hab-image" label="Home band photo (separate from About page hero)" value={get("home_about.image_url")} onChange={v => set("home_about.image_url", v)}/>
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Eyebrow"><Input data-testid="hab-eyebrow" value={get("home_about.eyebrow")} onChange={e => set("home_about.eyebrow", e.target.value)}/></Field>
              <Field label="CTA button label"><Input data-testid="hab-cta" value={get("home_about.cta_label")} onChange={e => set("home_about.cta_label", e.target.value)}/></Field>
              <Field label="Title"><Textarea rows={2} data-testid="hab-title" value={get("home_about.title")} onChange={e => set("home_about.title", e.target.value)}/></Field>
              <Field label="Vision title"><Input data-testid="hab-vt" value={get("home_about.vision_title")} onChange={e => set("home_about.vision_title", e.target.value)}/></Field>
            </div>
            <Field label="Subtitle (rich text)">
              <RichTextEditor testid="hab-subtitle" value={get("home_about.subtitle")} onChange={v => set("home_about.subtitle", v)}/>
            </Field>
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Vision text (rich text)">
                <RichTextEditor testid="hab-vtext" value={get("home_about.vision_text")} onChange={v => set("home_about.vision_text", v)}/>
              </Field>
              <Field label="Mission title"><Input data-testid="hab-mt" value={get("home_about.mission_title")} onChange={e => set("home_about.mission_title", e.target.value)}/></Field>
            </div>
            <Field label="Mission text (rich text)">
              <RichTextEditor testid="hab-mtext" value={get("home_about.mission_text")} onChange={v => set("home_about.mission_text", v)}/>
            </Field>
          </Section>
          <Section title="Leadership">
            <Field label="Section heading"><Input data-testid="ab-lh" value={get("about_page.leadership_heading")} onChange={e => set("about_page.leadership_heading", e.target.value)}/></Field>
            <RepeaterList testid="ab-leaders" addLabel="Add leader"
              items={get("about_page.leadership", [])} onChange={(arr) => set("about_page.leadership", arr)}
              makeNew={() => ({ id: `l${Date.now()}`, name: "", role: "", photo_url: "", initials: "" })}
              render={(it, idx, setI) => (
                <div className="grid sm:grid-cols-12 gap-3 items-start">
                  <div className="sm:col-span-3"><ImageUploader testid={`ab-leader-photo-${idx}`} label="Photo" value={it.photo_url} onChange={v => setI({ photo_url: v })}/></div>
                  <div className="sm:col-span-3"><Input data-testid={`ab-leader-name-${idx}`} value={it.name || ""} onChange={e => setI({ name: e.target.value })} placeholder="Name"/></div>
                  <div className="sm:col-span-3"><Input data-testid={`ab-leader-role-${idx}`} value={it.role || ""} onChange={e => setI({ role: e.target.value })} placeholder="Role"/></div>
                  <div className="sm:col-span-3"><Input data-testid={`ab-leader-initials-${idx}`} value={it.initials || ""} onChange={e => setI({ initials: e.target.value.toUpperCase() })} placeholder="Initials (fallback)"/></div>
                </div>
              )}
            />
          </Section>
          <Section title="Core values">
            <Field label="Heading"><Input data-testid="ab-vh" value={get("about_page.values_heading")} onChange={e => set("about_page.values_heading", e.target.value)}/></Field>
            <RepeaterList testid="ab-values" addLabel="Add value"
              items={get("about_page.values", [])} onChange={(arr) => set("about_page.values", arr)}
              makeNew={() => ({ icon: "Heart", title: "", desc: "" })}
              render={(it, idx, setI) => (
                <div className="grid sm:grid-cols-12 gap-3">
                  <div className="sm:col-span-2"><IconSelect testid={`ab-value-icon-${idx}`} value={it.icon} onChange={v => setI({ icon: v })}/></div>
                  <div className="sm:col-span-3"><Input data-testid={`ab-value-title-${idx}`} value={it.title || ""} onChange={e => setI({ title: e.target.value })} placeholder="Title"/></div>
                  <div className="sm:col-span-7"><Input data-testid={`ab-value-desc-${idx}`} value={it.desc || ""} onChange={e => setI({ desc: e.target.value })} placeholder="Description"/></div>
                </div>
              )}
            />
          </Section>
        </TabsContent>

        <TabsContent value="partners" className="space-y-5">
          <Section title="Partners page hero">
            <div className="grid sm:grid-cols-3 gap-5">
              <Field label="Eyebrow"><Input data-testid="pn-eyebrow" value={get("partners_page.hero.eyebrow")} onChange={e => set("partners_page.hero.eyebrow", e.target.value)}/></Field>
              <Field label="Title"><Input data-testid="pn-title" value={get("partners_page.hero.title")} onChange={e => set("partners_page.hero.title", e.target.value)}/></Field>
              <Field label="Subtitle"><Input data-testid="pn-subtitle" value={get("partners_page.hero.subtitle")} onChange={e => set("partners_page.hero.subtitle", e.target.value)}/></Field>
              <Field label="Marquee heading"><Input data-testid="pn-marquee" value={get("partners_page.marquee_heading")} onChange={e => set("partners_page.marquee_heading", e.target.value)}/></Field>
            </div>
            <div className="text-xs text-ink-muted">Tip: Add individual partner brands (with logos) from the <strong>Partners</strong> page in the sidebar.</div>
          </Section>
        </TabsContent>

        <TabsContent value="contact" className="space-y-5">
          <Section title="Contact page hero">
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Eyebrow"><Input data-testid="ct-eyebrow" value={get("contact_page.hero.eyebrow")} onChange={e => set("contact_page.hero.eyebrow", e.target.value)}/></Field>
              <Field label="Title"><Textarea rows={2} data-testid="ct-title" value={get("contact_page.hero.title")} onChange={e => set("contact_page.hero.title", e.target.value)}/></Field>
            </div>
            <Field label="Subtitle (rich text)">
              <RichTextEditor testid="ct-subtitle" value={get("contact_page.hero.subtitle")} onChange={v => set("contact_page.hero.subtitle", v)}/>
            </Field>
          </Section>
        </TabsContent>

        <TabsContent value="emi" className="space-y-5">
          <Section title="EMI Calculator hero">
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Eyebrow"><Input data-testid="emi-eyebrow" value={get("emi_page.hero.eyebrow")} onChange={e => set("emi_page.hero.eyebrow", e.target.value)}/></Field>
              <Field label="Title"><Textarea rows={2} data-testid="emi-title" value={get("emi_page.hero.title")} onChange={e => set("emi_page.hero.title", e.target.value)}/></Field>
            </div>
            <Field label="Subtitle (rich text)">
              <RichTextEditor testid="emi-subtitle" value={get("emi_page.hero.subtitle")} onChange={v => set("emi_page.hero.subtitle", v)}/>
            </Field>
          </Section>
        </TabsContent>

        <TabsContent value="footer" className="space-y-5">
          <Section title="Footer brand & contact">
            <Field label="Footer blurb (rich text)">
              <RichTextEditor testid="fo-blurb" value={get("footer.blurb")} onChange={v => set("footer.blurb", v)}/>
            </Field>
            <Field label="Copyright text"><Input data-testid="fo-copy" value={get("footer.copyright")} onChange={e => set("footer.copyright", e.target.value)}/></Field>
            <div className="grid sm:grid-cols-3 gap-5">
              <Field label="Phone"><Input data-testid="fo-phone" value={get("footer.contact.phone")} onChange={e => set("footer.contact.phone", e.target.value)}/></Field>
              <Field label="Email"><Input data-testid="fo-email" value={get("footer.contact.email")} onChange={e => set("footer.contact.email", e.target.value)}/></Field>
              <Field label="WhatsApp"><Input data-testid="fo-wa" value={get("footer.contact.whatsapp")} onChange={e => set("footer.contact.whatsapp", e.target.value)}/></Field>
            </div>
            <Field label="Map embed URL"><Input data-testid="fo-map" value={get("footer.map_embed_url")} onChange={e => set("footer.map_embed_url", e.target.value)}/></Field>
          </Section>
          <Section title="Social links">
            <RepeaterList testid="fo-socials" addLabel="Add social"
              items={get("footer.socials", [])} onChange={(arr) => set("footer.socials", arr)}
              makeNew={() => ({ label: "", icon: "Linkedin", url: "" })}
              render={(it, idx, setI) => (
                <div className="space-y-3">
                  <div className="grid sm:grid-cols-12 gap-3">
                    <div className="sm:col-span-3"><Input data-testid={`fo-soc-label-${idx}`} value={it.label || ""} onChange={e => setI({ label: e.target.value })} placeholder="Label (e.g. LinkedIn)"/></div>
                    <div className="sm:col-span-9"><Input data-testid={`fo-soc-url-${idx}`} value={it.url || ""} onChange={e => setI({ url: e.target.value })} placeholder="https://…"/></div>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-ink-muted mb-2 inline-block">Pick an icon</Label>
                    <div className="flex flex-wrap gap-2" data-testid={`fo-soc-iconpicker-${idx}`}>
                      {SOCIAL_ICONS.map(name => (
                        <button key={name} type="button" onClick={() => setI({ icon: name })}
                          data-testid={`fo-soc-icon-${idx}-${name}`}
                          className={`h-10 w-10 grid place-items-center rounded-full border transition-all ${it.icon === name ? "bg-navy text-saffron border-navy scale-110" : "bg-white text-navy border-slate-200 hover:border-saffron hover:text-saffron"}`}
                          title={name}>
                          <Icon name={name} className="h-4 w-4"/>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            />
          </Section>
          <Section title="Office addresses">
            <div className="text-xs text-ink-muted -mt-2 mb-1">Tick the box next to an office to display its address on the Contact page map. If none ticked, the manual Map embed URL above is used.</div>
            <RepeaterList testid="fo-offices" addLabel="Add office"
              items={get("footer.offices", [])} onChange={(arr) => set("footer.offices", arr)}
              makeNew={() => ({ heading: "", address: "", show_on_map: false })}
              render={(it, idx, setI) => (
                <div className="space-y-2">
                  <div className="grid sm:grid-cols-12 gap-3">
                    <div className="sm:col-span-3"><Input data-testid={`fo-office-h-${idx}`} value={it.heading || ""} onChange={e => setI({ heading: e.target.value })} placeholder="Heading"/></div>
                    <div className="sm:col-span-9"><Input data-testid={`fo-office-a-${idx}`} value={it.address || ""} onChange={e => setI({ address: e.target.value })} placeholder="Address"/></div>
                  </div>
                  <label className="inline-flex items-center gap-2 text-xs font-semibold text-navy cursor-pointer select-none">
                    <input type="checkbox" data-testid={`fo-office-map-${idx}`}
                      checked={!!it.show_on_map}
                      onChange={(e) => {
                        // Only one office can be the active map pin; clear others.
                        const all = get("footer.offices", []).map((o, i2) => ({ ...o, show_on_map: i2 === idx ? e.target.checked : false }));
                        set("footer.offices", all);
                      }}
                      className="h-4 w-4 accent-saffron"/>
                    Show this address on map (auto-syncs Contact page map)
                  </label>
                </div>
              )}
            />
          </Section>
          <Section title="Link columns">
            <RepeaterList testid="fo-cols" addLabel="Add column"
              items={get("footer.columns", [])} onChange={(arr) => set("footer.columns", arr)}
              makeNew={() => ({ heading: "", links: [] })}
              render={(it, idx, setI) => (
                <div className="space-y-2">
                  <Input data-testid={`fo-col-h-${idx}`} value={it.heading || ""} onChange={e => setI({ heading: e.target.value })} placeholder="Column heading"/>
                  <RepeaterList testid={`fo-col-links-${idx}`} addLabel="Add link"
                    items={it.links || []} onChange={(arr) => setI({ links: arr })}
                    makeNew={() => ({ label: "", url: "" })}
                    render={(lk, j, setJ) => (
                      <div className="grid sm:grid-cols-2 gap-3">
                        <Input value={lk.label || ""} onChange={e => setJ({ label: e.target.value })} placeholder="Label"/>
                        <Input value={lk.url || ""} onChange={e => setJ({ url: e.target.value })} placeholder="URL"/>
                      </div>
                    )}
                  />
                </div>
              )}
            />
          </Section>
          <Section title="Policy links (Privacy, Terms…)">
            <RepeaterList testid="fo-policy" addLabel="Add link"
              items={get("footer.policy_links", [])} onChange={(arr) => set("footer.policy_links", arr)}
              makeNew={() => ({ label: "", url: "" })}
              render={(it, idx, setI) => (
                <div className="grid sm:grid-cols-2 gap-3">
                  <Input data-testid={`fo-pol-l-${idx}`} value={it.label || ""} onChange={e => setI({ label: e.target.value })} placeholder="Label"/>
                  <Input data-testid={`fo-pol-u-${idx}`} value={it.url || ""} onChange={e => setI({ url: e.target.value })} placeholder="URL"/>
                </div>
              )}
            />
            <div className="text-xs text-ink-muted">Don't have a Privacy/Terms page yet? Go to <strong>Pages → New page</strong> and create one with slug <code>privacy</code> / <code>terms</code>.</div>
          </Section>
        </TabsContent>

        <TabsContent value="whatsapp" className="space-y-5">
          <Section title="Floating WhatsApp button">
            <div className="flex items-center justify-between rounded-xl bg-slate-50 p-3">
              <div><div className="text-sm font-medium">Show floating button</div><div className="text-xs text-ink-muted">Site-wide WhatsApp chat shortcut.</div></div>
              <Switch data-testid="fw-enabled" checked={!!get("floating_whatsapp.enabled", true)} onCheckedChange={v => set("floating_whatsapp.enabled", v)}/>
            </div>
            <div className="grid sm:grid-cols-3 gap-5">
              <Field label="WhatsApp number (intl)"><Input data-testid="fw-number" value={get("floating_whatsapp.number")} onChange={e => set("floating_whatsapp.number", e.target.value)} placeholder="+919876543210"/></Field>
              <Field label="Default message"><Input data-testid="fw-msg" value={get("floating_whatsapp.message")} onChange={e => set("floating_whatsapp.message", e.target.value)}/></Field>
              <Field label="Position">
                <Select value={get("floating_whatsapp.position", "right")} onValueChange={v => set("floating_whatsapp.position", v)}>
                  <SelectTrigger data-testid="fw-pos"><SelectValue/></SelectTrigger>
                  <SelectContent><SelectItem value="right">Right</SelectItem><SelectItem value="left">Left</SelectItem></SelectContent>
                </Select>
              </Field>
            </div>
          </Section>
        </TabsContent>
      </Tabs>

      <div className="flex justify-end">
        <Button data-testid="content-save-bottom" className="btn-saffron rounded-full" disabled={saving} onClick={save}>
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin"/> : <Save className="h-4 w-4 mr-2"/>}
          Save changes
        </Button>
      </div>
    </div>
  );
}
